<template>
  <div class="Paginator">
    <div v-if="!this.numberOfFields">
      <div v-if="position != 'bottom' && noRulesAdded">
        <hr />
        <h4 class="d-flex justify-content-center">
          {{ this.noResultsMessage }}
        </h4>
        <dd
          v-if="this.noResultsMessage == 'No Results'"
          class="d-flex justify-content-center"
        >
          Try choosing a different parameter from the dropdown menu.
        </dd>
        <dd
          v-else-if="this.perPageType == 'ruleset'"
          class="d-flex justify-content-center"
        >
          Begin by clicking Add Rule.
        </dd>
        <dd
          v-else-if="this.perPageType == 'sourcefieldmanager'"
          class="d-flex justify-content-center"
        >
          Begin by clicking Add Source Field.
        </dd>
      </div>
    </div>
    <div v-else>
      <div class="row d-flex mt-3">
        <div v-if="position == 'bottom'">
          <div class="row d-flex mt-3">
            <!-- Items displayed (for top) -->
            <div class="col-lg-6">
              <div class="d-flex justify-content-start">
                {{
                  this.numberOfFields
                    ? this.pageStart +
                      1 +
                      "-" +
                      (this.pageStart + this.loadPerPage > this.numberOfFields
                        ? this.numberOfFields
                        : this.pageStart + this.loadPerPage) +
                      " of " +
                      this.numberOfFields +
                      " fields"
                    : ""
                }}
              </div>
            </div>
            <div class="col-lg-6">
              <div class="d-flex justify-content-end">
                <button @click="scroll('top')" class="btn" style="padding:0px">
                  <font-awesome-icon icon="fa-solid fa-arrow-up" />
                </button>
              </div>
            </div>
          </div>
          <hr />
        </div>
        <!-- "Sort By" selector -->
        <div class="col-lg-12 col-xl-2">
          <select
            @change="reverseOrder"
            class="form-select"
            style="width:140px;"
            v-model="reverseSort"
          >
            <option selected value="false">Oldest First</option>
            <option value="true">Newest First</option>
          </select>
        </div>
        <!-- Pagination menu -->
        <div class="col-lg-12 col-xl-6">
          <div class="pagination d-flex justify-content-center no-select">
            <div
              :class="{ disabledlink: activePageNumber == 1 }"
              class="pointer-cursor"
              style="display:inline"
            >
              <a
                v-show="jumpPages()"
                @click="goToPage(activePageNumber - jumpPages())"
              >
                &laquo; {{ jumpPages() }}
              </a>
              <a
                v-show="this.numberOfFields"
                @click="goToPage(activePageNumber - 1)"
              >
                Previous
              </a>
            </div>
            <!-- Pagination case 0 (Base case): Less than 10 pages -->
            <div v-if="numberOfPages < numPagesInSequence + 3">
              <div
                v-for="pageNum in numberOfPages"
                :key="pageNum"
                style="display:inline"
              >
                <a
                  v-bind:class="{ active: pageNum == activePageNumber }"
                  @click="goToPage(pageNum)"
                  >{{ pageNum }}</a
                >
              </div>
            </div>
            <!-- Pagination case 1: Active page 1-5 -->
            <div v-else-if="activePageNumber < numPagesInSequence - 1">
              <div
                v-for="pageNum in numPagesInSequence"
                :key="pageNum"
                class="pointer-cursor"
                style="display:inline"
              >
                <a
                  v-bind:class="{ active: pageNum == activePageNumber }"
                  @click="goToPage(pageNum)"
                  >{{ pageNum }}</a
                >
              </div>
              <a class="btn disabled ellipsis-button">...</a>
              <a
                v-bind:class="{ active: numberOfPages == activePageNumber }"
                @click="goToPage(numberOfPages)"
                class="pointer-cursor"
                >{{ numberOfPages }}</a
              >
            </div>
            <!-- Pagination case 2: Active page 41-45 -->
            <div
              v-else-if="
                activePageNumber > numberOfPages - (numPagesInSequence - 2)
              "
            >
              <a
                v-bind:class="{ active: 1 == activePageNumber }"
                @click="goToPage(1)"
                class="pointer-cursor"
                >1</a
              >
              <a class="btn disabled ellipsis-button">...</a>
              <div
                v-for="pageNum in numPagesInSequence"
                :key="pageNum"
                class="pointer-cursor"
                style="display:inline"
              >
                <a
                  v-bind:class="{
                    active:
                      numberOfPages + pageNum - numPagesInSequence ==
                      activePageNumber,
                  }"
                  @click="
                    goToPage(numberOfPages + pageNum - numPagesInSequence)
                  "
                  >{{ numberOfPages + pageNum - numPagesInSequence }}</a
                >
              </div>
            </div>
            <!-- Pagination case 3: Active page 6-40 (or n) -->
            <div v-else>
              <a
                v-bind:class="{ active: 1 == activePageNumber }"
                @click="goToPage(1)"
                class="pointer-cursor"
                >1</a
              >
              <a class="btn disabled ellipsis-button">...</a>
              <div
                v-for="pageNum in numPagesInSequence - 2"
                :key="pageNum"
                class="pointer-cursor"
                style="display:inline"
              >
                <a
                  v-bind:class="{
                    active:
                      activePageNumber +
                        pageNum -
                        (numPagesInSequence - 4 + smallerWidthAddOne) ==
                      activePageNumber,
                  }"
                  @click="
                    goToPage(
                      activePageNumber +
                        pageNum -
                        (numPagesInSequence - 4 + smallerWidthAddOne)
                    )
                  "
                  >{{
                    activePageNumber +
                      pageNum -
                      (numPagesInSequence - 4 + smallerWidthAddOne)
                  }}</a
                >
              </div>
              <a class="btn disabled ellipsis-button">...</a>
              <a
                v-bind:class="{ active: numberOfPages == activePageNumber }"
                @click="goToPage(numberOfPages)"
                class="pointer-cursor"
                >{{ numberOfPages }}</a
              >
            </div>
            <div
              :class="{ disabledlink: activePageNumber == numberOfPages }"
              class="pointer-cursor"
              style="display:inline"
            >
              <a
                v-show="this.numberOfFields"
                @click="goToPage(activePageNumber + 1)"
                >Next</a
              >
              <a
                v-show="jumpPages()"
                @click="goToPage(activePageNumber + jumpPages())"
                >{{ jumpPages() }} &raquo;</a
              >
            </div>
          </div>
        </div>
        <!-- Choose specific page -->
        <div class="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-2">
          <form
            @submit.prevent="goToPage($event.target.value)"
            class="right-justified me-3"
            style="padding:8px"
          >
            Page
            <input
              type="number"
              @input="goToPage($event.target.value)"
              :value="activePageNumber"
              :placeholder="this.activePageNumber"
              min="1"
              :max="this.numberOfPages"
              class="input-sm"
              style="width:50px; height:22px"
              size="2"
            />
            {{ " of " + numberOfPages }}
          </form>
        </div>
        <!-- Number of Items Per Page -->
        <div class="col-6 col-sm-6 col-md-6 col-lg-6 col-xl-2">
          <div
            class="d-flex align-items-center align-self-center justify-content-end"
            id="chooseNumItemsPerPage"
          >
            <label class="align-self-center">Items per page:</label>
            <!-- Items per page for Source Field Manager -->
            <div v-if="perPageType == 'sourcefieldmanager'">
              <select
                @change="itemsPerPageUpdated"
                v-model.number="loadPerPage"
                class="form-select-sm mt-2 mb-2 ms-2 align-self-center"
                aria-label="Show per page"
              >
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="75">75</option>
                <option value="100">100</option>
                <option value="150">150</option>
                <option value="300">300</option>
                <option value="500">500</option>
              </select>
            </div>
            <!-- Items per page for Rulesets -->
            <div v-else>
              <select
                @change="itemsPerPageUpdated"
                v-model.number="loadPerPage"
                class="form-select-sm mt-2 mb-2 ms-2 align-self-center"
                aria-label="Show per page"
              >
                <option value="5">5</option>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="75">75</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>
        </div>
        <div v-if="position == 'top' || position == 'modal'">
          <hr />
          <!-- Items displayed (for top) -->
          <div class="row d-flex mb-3">
            <div class="col-lg-6">
              <div class="d-flex justify-content-start">
                {{
                  this.pageStart +
                    1 +
                    "-" +
                    (this.pageStart + this.loadPerPage > this.numberOfFields
                      ? this.numberOfFields
                      : this.pageStart + this.loadPerPage) +
                    " of " +
                    this.numberOfFields +
                    " fields"
                }}
              </div>
            </div>
            <div class="col-lg-6">
              <div
                v-if="position !== 'modal'"
                class="d-flex justify-content-end"
              >
                <button
                  @click="scroll('bottom')"
                  class="btn"
                  style="padding:0px"
                >
                  <font-awesome-icon icon="fa-solid fa-arrow-down" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Paginator",
  props: [
    "position",
    "array",
    "numberOfFields",
    "activePageNumberTemp",
    "loadPerPageTemp",
    "pageStart",
    "numberOfPages",
    "reverseSortTemp",
    "parentWidth",
    "noResultsMessageTemp",
    "perPageType",
  ],
  data() {
    return {
      loadPerPage: this.loadPerPageTemp,
      reverseSort: this.reverseSortTemp,
      activePageNumber: 1,
      width: this.calculateWidth(),
      noResultsMessage: this.noResultsMessageTemp
        ? this.noResultsMessageTemp
        : "No Results",
      noRulesAdded: true,
    };
  },
  // To get the window size
  created() {
    window.addEventListener("resize", this.onResize);
  },
  destroyed() {
    window.removeEventListener("resize", this.onResize);
  },
  computed: {
    numPagesInSequence() {
      if (this.width > 1600) return 7;
      else return 5;
    },
    smallerWidthAddOne() {
      if (this.width > 1600) return 0;
      else return 1;
    },
  },
  watch: {
    // Updates when size of rule array changes
    noResultsMessageTemp() {
      this.noResultsMessage = this.noResultsMessageTemp;
    },
    // Syncs load per page bar across multiple instances
    loadPerPageTemp() {
      this.loadPerPage = this.loadPerPageTemp;
    },
    reverseSortTemp() {
      this.reverseSort = this.reverseSortTemp;
    },
    activePageNumberTemp() {
      this.activePageNumber = this.activePageNumberTemp;
    },
    numberOfPages() {
      // Prevents staying on nonexistent last page if the only rule on the last page is removed
      // The if statement prevents the view from jumping to the bottom when changing number of pages
      if (this.position != "bottom") {
        this.goToPage(this.activePageNumberTemp);
      }
    },
  },
  methods: {
    calculateWidth() {
      if (this.parentWidth == null) {
        return window.innerWidth;
      } else {
        return this.parentWidth;
      }
    },
    // To get the window size to dynamically render pagination bar
    onResize() {
      if (this.parentWidth == null) {
        this.width = window.innerWidth;
      }
    },
    reverseOrder() {
      this.$emit("updateReverseSort", this.reverseSort, this.position);
    },
    itemsPerPageUpdated() {
      this.$emit(
        "itemsPerPageUpdated",
        Math.max(1, Math.ceil((this.pageStart + 1) / this.loadPerPage)),
        Math.max(
          0,
          Math.max(1, Math.ceil((this.pageStart + 1) / this.loadPerPage)) - 1
        ) * this.loadPerPage,
        this.loadPerPage,
        this.position
      );
    },
    goToPage(newPage) {
      newPage = parseInt(newPage);
      if (!isNaN(newPage)) {
        if (newPage < 1) {
          newPage = 1;
        } else if (newPage > this.numberOfPages) {
          newPage = this.numberOfPages;
        }
        this.$emit(
          "goToPage",
          newPage,
          Math.max(0, newPage - 1) * this.loadPerPage,
          this.position
        );
      }
    },
    jumpPages() {
      if (this.numberOfPages < 10) {
        // No jump option
        return false;
      } else if (this.numberOfPages < 25) {
        return 5;
      } else if (this.numberOfPages < 50) {
        return 10;
      } else if (this.numberOfPages < 100) {
        return 25;
      } else if (this.numberOfPages < 500) {
        return 50;
      } else if (this.numberOfPages < 1000) {
        return 100;
      } else {
        return 250;
      }
    },
    scroll(whereTo) {
      if (whereTo == "bottom") {
        window.scrollTo(
          0,
          document.body.scrollHeight || document.documentElement.scrollHeight
        );
      } else if (whereTo == "top") {
        window.scrollTo(0, 0);
      } else {
        console.warn("No scroll location specified!");
      }
    },
  },
};
</script>

<style>
.pagination {
  display: inline-block;
}

.pagination a {
  color: black;
  float: left;
  padding: 8px 16px;
  text-decoration: none;
  transition: background-color 0.3s;
}

.pagination a.active {
  background-color: rgb(0, 183, 255);
  color: white;
}

.pagination a:hover:not(.active) {
  background-color: #ddd;
}

#chooseNumItemsPerPage {
  display: inline;
}

.disabledlink {
  cursor: not-allowed !important;
  color: #7a7a7a !important;
}

.ellipsis-button {
  padding: 5px !important;
}

/* Disables selector cursor for all browsers */
.no-select {
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently supported by Chrome, Edge, Opera and Firefox */
}

.pointer-cursor {
  cursor: pointer;
}
</style>
